import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useWeekdayNames } from '../analytics';
import {
  REPORT_OUTPUT_FORMATS,
  REPORT_SCHEDULE_FREQUENCIES,
  REPORT_SCHEDULE_STATUSES,
  type ReportOutputFormat,
  type ReportSchedule,
  type ReportScheduleFrequency,
  type ReportScheduleStatus,
} from '../reporting-types';
import { updateSchedule } from '../reportingApi';
import { apiMessage } from '../lib';

/**
 * Edits a schedule's cadence, format and status. `savedReportId` and
 * `runAsUserId` are absent from this form because they are absent from
 * `UpdateScheduleDto` — the report a schedule delivers and the authority it
 * runs under are both fixed at creation (ADR-0013 §2.6).
 */
export function ScheduleEditDialog({
  schedule,
  onClose,
  onSaved,
}: {
  schedule: ReportSchedule | null;
  onClose: () => void;
  onSaved: (schedule: ReportSchedule) => void;
}) {
  const { t } = useTranslation();
  const weekdayName = useWeekdayNames();

  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState<ReportScheduleFrequency>('DAILY');
  const [hour, setHour] = useState(6);
  const [minute, setMinute] = useState(0);
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timezone, setTimezone] = useState('UTC');
  const [format, setFormat] = useState<ReportOutputFormat>('XLSX');
  const [linkTtlHours, setLinkTtlHours] = useState(168);
  const [status, setStatus] = useState<ReportScheduleStatus>('ACTIVE');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!schedule) return;
    setName(schedule.name);
    setFrequency(schedule.frequency);
    setHour(schedule.hour);
    setMinute(schedule.minute);
    setDayOfWeek(schedule.dayOfWeek ?? 0);
    setDayOfMonth(schedule.dayOfMonth ?? 1);
    setTimezone(schedule.timezone);
    setFormat(schedule.format);
    setLinkTtlHours(schedule.linkTtlHours);
    setStatus(schedule.status);
    setError(null);
  }, [schedule]);

  if (!schedule) return null;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateSchedule(schedule.id, {
        name: name.trim(),
        frequency,
        hour,
        minute,
        dayOfWeek: frequency === 'WEEKLY' ? dayOfWeek : undefined,
        dayOfMonth: frequency === 'MONTHLY' ? dayOfMonth : undefined,
        timezone,
        format,
        linkTtlHours,
        status,
      });
      toast.success(t('reporting.schedules.updated', { name: updated.name }));
      onSaved(updated);
      onClose();
    } catch (err) {
      setError(apiMessage(err, t('reporting.errors.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('reporting.schedules.edit')}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving || !name.trim()}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <Field label={t('reporting.schedules.name')} required>
          <Input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('reporting.schedules.frequency')}>
            <Select
              value={frequency}
              onChange={(event) => setFrequency(event.target.value as ReportScheduleFrequency)}
            >
              {REPORT_SCHEDULE_FREQUENCIES.map((value) => (
                <option key={value} value={value}>
                  {t(`reporting.frequency.${value}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('reporting.schedules.status')}>
            <Select value={status} onChange={(event) => setStatus(event.target.value as ReportScheduleStatus)}>
              {REPORT_SCHEDULE_STATUSES.filter((value) => value !== 'SUSPENDED' || status === 'SUSPENDED').map(
                (value) => (
                  <option key={value} value={value}>
                    {t(`reporting.scheduleStatus.${value}`)}
                  </option>
                ),
              )}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t('reporting.schedules.hour')}>
            <Input
              type="number"
              min={0}
              max={23}
              value={hour}
              onChange={(event) => setHour(Number(event.target.value))}
            />
          </Field>
          <Field label={t('reporting.schedules.minute')}>
            <Input
              type="number"
              min={0}
              max={59}
              value={minute}
              onChange={(event) => setMinute(Number(event.target.value))}
            />
          </Field>
          {frequency === 'WEEKLY' && (
            <Field label={t('reporting.schedules.dayOfWeek')}>
              <Select value={dayOfWeek} onChange={(event) => setDayOfWeek(Number(event.target.value))}>
                {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                  <option key={day} value={day}>
                    {weekdayName(day)}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {frequency === 'MONTHLY' && (
            <Field label={t('reporting.schedules.dayOfMonth')}>
              <Input
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                onChange={(event) => setDayOfMonth(Number(event.target.value))}
              />
            </Field>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('reporting.schedules.timezone')}>
            <Input value={timezone} onChange={(event) => setTimezone(event.target.value)} />
          </Field>
          <Field label={t('reporting.export.format')}>
            <Select value={format} onChange={(event) => setFormat(event.target.value as ReportOutputFormat)}>
              {REPORT_OUTPUT_FORMATS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label={t('reporting.schedules.linkTtl')} hint={t('reporting.schedules.linkTtlHint')}>
          <Input
            type="number"
            min={1}
            max={720}
            value={linkTtlHours}
            onChange={(event) => setLinkTtlHours(Number(event.target.value))}
          />
        </Field>
      </div>
    </Dialog>
  );
}
